import React, { useState, useEffect, useRef } from "react";
import "./coremenudisk3d_v2.css";
import MatrixPromo from './matrixpromo';
import Christmas from './Christmas';

/**
 * BragaBJJ Holographic Disk Menu v2
 * Center orb with 3D rotation and radial menu icons.
 * Works on desktop and mobile.
 */

export default function CorediskMenu3D_v2({ onSelect, wallet, isAdmin, onConnectWallet, onShowPinModal }) {
  const [open, setOpen] = useState(false);
  const diskRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [locked, setLocked] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [promoKey, setPromoKey] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const menuOptions = [
    { icon: "💬", label: "Student Tavern", desc: "Community & learning chat", action: "tavern" },
    { icon: "🥋", label: "Instructor", desc: "Instructor Chat", action: "instructor" },
    { icon: "🎓", label: "Student Portal", desc: "Profile, belts, awards & messaging", action: "studentportal" },
    { icon: "📥", label: "Leads", desc: "Leads manager & messaging", action: "leads" },
    { icon: "🧠", label: "Control Center", desc: "Admin access & management", action: "controlcenter", admin: true },
    { icon: "🔗", label: "Connect Wallet", desc: "Link MetaMask for full features", action: "connect-wallet", wallet: true },
    { icon: "🎟️", label: "Matrix Rashguard", desc: "Limited merch drop & NFT collectible", action: "blackfriday" },
    { icon: "🎄", label: "Christmas", desc: "Holiday raffle & gift box", action: "christmas" },
    { icon: "🆓", label: "Free Trial", desc: "Register new students & leads", action: "freetrial" },
    { icon: "🏫", label: "Kiosk Mode", desc: "Attendance & on-site tools", action: "kiosk" },
    { icon: "⚙️", label: "Settings", desc: "Preferences & customization", action: "settings" },
  ];

  // Handle 3D tilt
  useEffect(() => {
    const handleMove = (e) => {
      const rect = diskRef.current?.getBoundingClientRect();
      const isMobile = (typeof window !== 'undefined') ? window.innerWidth < 768 : false;
      if (!rect || isMobile) return; // disable tilt on mobile
      const x = (e.clientX - rect.left - rect.width / 2) / 25;
      const y = (e.clientY - rect.top - rect.height / 2) / 25;
      setTilt({ x, y });
    };
    const handleLeave = () => setTilt({ x: 0, y: 0 });
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseleave", handleLeave);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  // Open menu on disk click
  const handleDiskClick = async () => {
    if (locked || connecting) return;
    
    // If no wallet connected, prompt for connection first
    if (!wallet && onConnectWallet) {
      try {
        setConnecting(true);
        console.log('🔗 CorediskMenu: Initiating wallet connection...');
        await onConnectWallet();
        // Don't auto-open menu - let TronWalletModal handle the flow
      } catch (err) {
        console.warn('Wallet connection cancelled or failed:', err);
      } finally {
        setConnecting(false);
      }
      return;
    }
    
    // If wallet already connected, just toggle menu
    setOpen((prev) => !prev);
  };

  return (
    <div className="core3d-container">
      {/* Animated water drop background */}
      <div className="core3d-bg-water"></div>
      <div
        ref={diskRef}
        className={`core3d-disk ${open ? "open" : ""} ${connecting ? "connecting" : ""}`}
        onClick={handleDiskClick}
        style={{
          transform: `perspective(800px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
        }}
      >
        <div className="core3d-glow"></div>
        <div className="core3d-center">
          {connecting ? (
            <>
              <div className="core3d-logo core3d-logo-big core3d-logo-pulse">🦊</div>
              <div className="core3d-title">Connecting...</div>
              <div className="core3d-motto">Check MetaMask</div>
            </>
          ) : (
            <>
              <div className="core3d-logo core3d-logo-big core3d-logo-pulse">01</div>
              <div className="core3d-title">Outbox BJJ</div>
              <div className="core3d-motto">Evolve • Empower • Excel</div>
              <div className="core3d-champion">World Champion School</div>
            </>
          )}
        </div>
      </div>

      {open && !locked && (
        <div className="core3d-ring">
          {menuOptions.map((opt, i) => (
            <div
              key={i}
              className="core3d-item"
              style={{
                transform: `rotate(${i * (360 / menuOptions.length)}deg) translate(14rem) rotate(-${i * (360 / menuOptions.length)}deg)`,
              }}
              onClick={() => {
                if (opt.action === 'blackfriday' || opt.action === 'christmas') {
                  setPromoKey(opt.action);
                  setShowPromo(true);
                  setOpen(false);
                  return;
                }
                if (opt.action === 'connect-wallet' && onConnectWallet) {
                  onConnectWallet().then(() => setOpen(false)).catch(() => setOpen(false));
                  return;
                }
                if (opt.admin && !isAdmin && onShowPinModal) {
                  onShowPinModal();
                  setOpen(false);
                  return;
                }
                onSelect(opt.action);
                setOpen(false);
              }}
            >
              <div className="core3d-item-inner">
                <span className="core3d-icon">{opt.icon}</span>
                <div className="core3d-label">{opt.label}</div>
                <div className="core3d-desc">{opt.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Black Friday / Promo modal rendered inline when requested */}
      {showPromo && (
        <div className="tron-modal-overlay tron-animate-frame" onClick={() => { setShowPromo(false); setPromoKey(null); }}>
          <div className="tron-modal-glass tron-animate-border" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 920, width: '95%' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="tron-btn tron-animate-btn" onClick={() => { setShowPromo(false); setPromoKey(null); }}>Close</button>
            </div>
            {promoKey === 'blackfriday' && <MatrixPromo onClose={() => { setShowPromo(false); setPromoKey(null); }} inline={true} />}
            {promoKey === 'christmas' && <Christmas onClose={() => { setShowPromo(false); setPromoKey(null); }} inline={true} />}
          </div>
        </div>
      )}
    </div>
  );
}
